import { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { FormField, FormHelp, Input, Label } from '@zillow/constellation-10';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../../components/Wizard/context';

import { AppSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';

import { useForm } from '../../../../hooks';

import PropertiesActions from '../../../../actions/PropertiesActions';
import PostingActions from '../../../../actions/PostingActions';
import actions from '../../../../containers/CreateListingPage/actions';
import wizardActions from '../../../../components/Wizard/actions';

import { postingConstraints } from '../../../../utils/formValidation';

import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';

import WizardServerError from '../../../../model/WizardServerError';

import WizardAlert from '../../../../components/WizardAlert';

import type { ChangeEvent } from 'react';
import type { MockEventProps } from './interfaces';

const inputName = 'contactName';

const NameFormField = () => {
  const dispatch = useDispatch();

  const { reducerWizardDispatch } = useContext(WizardContext);
  const {
    modularListingState: { isInModalState, isSaveComplete, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const { contactName, listingAlias } = useSelector((state) => state.properties.currentProperty);
  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));

  const {
    inputs,
    errorObj,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      contactName,
    },
    postingConstraints,
  );

  useEffect(() => {
    if (isSaveComplete) {
      // eslint-disable-next-line no-use-before-define
      save({
        target: {
          name: inputName,
          // @ts-expect-error
          value: inputs.contactName,
        },
      });
    }
  }, [isSaveComplete]);

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event: ChangeEvent<HTMLInputElement> | MockEventProps) => {
    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    const { value, name: fieldName } = event.target;

    if (hasFieldError(fieldName, value)) {
      actions.updateHasFieldError({ reducerDispatch, hasFieldError: true });
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      return;
    }

    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });

    const dataToSave = {
      fieldName,
      value,
      propertyId: listingAlias,
    };

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    dispatch(PostingActions.property.edit(dataToSave))
      // @ts-expect-error
      .then(() => {
        dispatch(PropertiesActions.updateCurrentProperty({ [fieldName]: value }));

        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((message: string) => {
        updateServerError(message, fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement> | MockEventProps) => {
    clearServerError();
    // @ts-expect-error
    handleFormChange(event);
  };

  return (
    <>
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

      <FormField
        data-testid="NameFormField"
        label={<Label required={true}>Name</Label>}
        // @ts-expect-error
        error={!!errorObj.contactName}
        control={
          <Input
            size={isScreenDesktop ? 'lg' : 'md'}
            name={inputName}
            value={inputs.contactName as string}
            onChange={handleChange}
            onBlur={save}
            // @ts-expect-error
            error={!!errorObj.contactName}
            className="fs-mask"
          />
        }
        // @ts-expect-error
        description={<FormHelp>{errorObj.contactName}</FormHelp>}
      />
    </>
  );
};

export default NameFormField;
